import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

type Source = 'grants' | 'admin_activity' | 'login_logs';

const AuditSearch: React.FC = () => {
    const { api } = useInternalApi();
    const [source, setSource] = useState<Source>('grants');
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Global Audit Search - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ source });
            if (search) params.set('search', search);
            const res = await api.get(`platform/admin/audit-search/?${params}`);
            if (res.data?.status === 'success') setRows(res.data.data || []);
        } catch { setRows([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faSearch} className="me-2 text-primary" />Global Audit Search</h2>
            <p className="text-muted mb-4">Search across dashboard grant history, admin actions and login attempts</p>

            <div className="d-flex gap-2 mb-3">
                <ul className="nav nav-pills">
                    <li className="nav-item"><button className={`nav-link ${source === 'grants' ? 'active' : ''}`} onClick={() => setSource('grants')}>Dashboard Grants</button></li>
                    <li className="nav-item"><button className={`nav-link ${source === 'admin_activity' ? 'active' : ''}`} onClick={() => setSource('admin_activity')}>Admin Activity</button></li>
                    <li className="nav-item"><button className={`nav-link ${source === 'login_logs' ? 'active' : ''}`} onClick={() => setSource('login_logs')}>Login Logs</button></li>
                </ul>
            </div>

            <div className="input-group mb-3" style={{ maxWidth: 420 }}>
                <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                <input className="form-control" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
                <button className="btn btn-outline-primary" onClick={load}>Search</button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-5 text-muted">No results.</div>
                    ) : source === 'grants' ? (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Principal</th><th>Dashboard</th><th>Granted By</th><th>Granted</th><th>Revoked</th></tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.principal_type}:{r.principal_id}</td>
                                        <td>{r.dashboard_label}</td>
                                        <td>{r.granted_by_type ? `${r.granted_by_type}:${r.granted_by_id}` : <span className="text-muted">self-service</span>}</td>
                                        <td>{new Date(r.granted_at).toLocaleString()}</td>
                                        <td>{r.revoked_at ? new Date(r.revoked_at).toLocaleString() : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : source === 'admin_activity' ? (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Actor</th><th>Action</th><th>Target</th><th>Description</th><th>When</th></tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.actor || '—'}</td>
                                        <td><span className="badge bg-secondary">{r.action}</span></td>
                                        <td>{r.target_model} #{r.target_id}</td>
                                        <td>{r.description}</td>
                                        <td>{new Date(r.timestamp).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>User</th><th>Type</th><th>Result</th><th>IP</th><th>Clinic</th><th>When</th></tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.user_email}</td>
                                        <td>{r.user_type}</td>
                                        <td><span className={`badge ${r.login_successful ? 'bg-success' : 'bg-danger'}`}>{r.login_successful ? 'Success' : 'Failed'}</span></td>
                                        <td>{r.ip_address || '—'}</td>
                                        <td>{r.clinic || '—'}</td>
                                        <td>{new Date(r.created).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </InternalShell>
    );
};

export default AuditSearch;
